import { useState } from 'react';
import { type QueryRef, useReadQuery } from '@apollo/client/react';
import { Link } from 'react-router';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { type IAppsQuery, type IAppsQueryVariables } from 'src/graphql/actions/apps/apps.query';
import { withSuspense } from 'src/hoc';

import ApplicationIllustration from './ApplicationIllustration';

const NoFallback = () => null;

type Props = {
  unclaimedQueryRef: QueryRef<IAppsQuery, IAppsQueryVariables>;
};

const UnclaimedBanner = ({ unclaimedQueryRef }: Props) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const {
    data: { apps },
  } = useReadQuery(unclaimedQueryRef);

  const handleDismiss = () => setIsDismissed(true);

  if (!apps.nodesCount || isDismissed) return null;

  const isSingle = apps.nodesCount === 1;

  return (
    <Alert className="flex items-start gap-6 rounded-xl p-4 bg-accent-software-escrow">
      <div className="flex min-w-0 grow flex-col gap-y-1.5">
        <div>
          <Badge variant="pill" className="bg-primary text-secondary-foreground px-2 py-1 border-0 font-bold">
            New
          </Badge>
        </div>
        <AlertTitle className="text-lg font-extrabold text-white">
          {isSingle
            ? 'Your protection is now in its own application'
            : 'Your protections are now grouped by application'}
        </AlertTitle>
        <AlertDescription className="space-y-1 text-white leading-relaxed font-medium">
          <p>
            We’ve organized Codekeeper around the software you protect. An application is one piece of software and
            everything protecting it: its Software Escrow, its Software Backup, its Software Custodian.
          </p>
          <p>
            {isSingle
              ? 'We’ve placed your protection in its own application. Review it when you’re ready: finish setup, create new applications, or merge it into another that covers the same software. Your agreement hasn’t changed.'
              : `We’ve placed each of your ${apps.nodesCount} protections in its own application. Review them when you’re ready: finish setup, create new applications, or merge any that cover the same software. Your agreements haven’t changed.`}
          </p>
        </AlertDescription>
        <div className="mt-2 flex gap-2">
          <Button asChild className="bg-white text-primary-foreground hover:bg-white/88 hover:text-primary-foreground">
            <Link to="/applications">View applications</Link>
          </Button>
          <Button variant="ghost" className="border border-white text-white hover:bg-white/12" onClick={handleDismiss}>
            Got it
          </Button>
        </div>
      </div>
      <div className="hidden w-52.5 shrink-0 md:block">
        <ApplicationIllustration />
      </div>
    </Alert>
  );
};

export default withSuspense(UnclaimedBanner, NoFallback);
